import React, { useContext, useState } from 'react';
import Animation from '../../Animation';
import Select from '../../engine/SelectEngine';
import InputBase from '../../form/InputBase';
import { FieldsQueryBuilderContext } from '../engine/FieldsQueryBuilderEngine';
import styled from 'styled-components/macro';
import {
  unwrapPercents,
  wrapPercents
} from '../../../internal-components/conditional-logic/wrapPercents';
import DateComparisonEngine from '../engine/DateComparisonEngine';

const AlignmentSpan = styled.span`
  display: flex;
  align-items: center;

  & > :nth-child(2) {
    margin-left: 0.8rem;
  }

  ${({ $customCSS }) => $customCSS}
`;

const FieldsConditionsQueryBuilderView = props => {
  const { fieldValue, fields, form, variant } = useContext(
    FieldsQueryBuilderContext
  );
  switch (props.compareType) {
    case 'yes_no':
    case 'exact':
      return (
        <ExactComparison
          fields={fields}
          optionsSourceValue={fieldValue}
          variant={variant}
          {...props}
        />
      );
    case 'text':
      return <TextComparison variant={variant} {...props} />;
    case 'number':
      return <NumberComparison variant={variant} {...props} />;
    case 'date':
      return <DateComparisonEngine variant={variant} {...props} />;
    case 'confirmation':
      return <ConfirmationComparison variant={variant} {...props} />;
    case 'multiple_choice':
      return (
        <MultipleChoiceComparison
          fields={fields}
          form={form}
          optionsSourceValue={fieldValue}
          variant={variant}
          {...props}
        />
      );

    default:
      return <>s</>;
  }
};

const TextComparison = ({
  updateVal = () => {},
  updateOp = () => {},
  op,
  value,
  variant = 'outlined'
}) => {
  let initialOpValue;
  if (value === '' && (op === 'blank' || op === '=')) {
    initialOpValue = 'blank';
  } else {
    initialOpValue = op;
  }
  const [liveVal, setLiveVal] = useState(value);
  const [displayOp, setDisplayOp] = useState(initialOpValue);

  return (
    <AlignmentSpan>
      <Select
        data-testid="logicbuilder-text-comparison-select-op"
        variant="inline"
        placeholder="Choose an option..."
        onChange={newDisplayOp => {
          if (newDisplayOp === 'blank') {
            updateOp('=');
            updateVal('');
            setLiveVal('');
          } else if (
            op === 'ILIKE' ||
            (op === 'NOT ILIKE' && newDisplayOp === 'ILIKE') ||
            newDisplayOp === 'NOT ILIKE'
          ) {
            updateOp(newDisplayOp);
            updateVal('');
            setLiveVal('');
          } else if (newDisplayOp === 'ILIKE' || newDisplayOp === 'NOT ILIKE') {
            updateOp(newDisplayOp);
            updateVal('');
            setLiveVal('');
          } else {
            updateOp(newDisplayOp);
            updateVal('');
            setLiveVal('');
          }
          setDisplayOp(newDisplayOp);
        }}
        value={displayOp}
        options={[
          {
            text: 'contains',
            value: 'ILIKE',
            'data-testid': `logicbuilder-text-comparison-select-op-option-ILIKE`
          },
          {
            text: 'does not contain',
            value: 'NOT ILIKE',
            'data-testid': `logicbuilder-text-comparison-select-op-option-NOTILIKE`
          },
          {
            text: 'is not blank',
            value: '<>',
            'data-testid': `logicbuilder-text-comparison-select-op-option-<>`
          },
          {
            text: 'is blank',
            value: 'blank',
            'data-testid': `logicbuilder-text-comparison-select-op-option-=`
          },
          {
            text: 'equals',
            value: '=',
            'data-testid': `logicbuilder-text-comparison-select-op-option-==`
          }
        ]}
      />
      <Animation
        in={
          displayOp === 'ILIKE' ||
          displayOp === 'NOT ILIKE' ||
          displayOp === '='
        }
        type="collapse"
        onExit={() => updateVal('')}
      >
        <InputBase
          variant={variant}
          value={unwrapPercents(liveVal)}
          onChange={e => setLiveVal(e.target.value)}
          onBlur={() => updateVal(liveVal || (op === '=' ? '' : null))}
          placeholder="Enter text"
        />
      </Animation>
    </AlignmentSpan>
  );
};

const NumberComparison = ({
  updateVal = () => {},
  updateOp = () => {},
  op,
  value,
  variant = 'outlined'
}) => {
  const [liveVal, setLiveVal] = useState(value);

  return (
    <AlignmentSpan>
      <Select
        data-testid="logicbuilder-text-comparison-select-op"
        variant="inline"
        onChange={updateOp}
        value={op}
        placeholder="Choose an option..."
        options={[
          {
            text: 'more than (>)',
            value: '>',
            'data-testid': `logicbuilder-number-comparison-select-op-option->`
          },
          {
            text: 'at least (>=)',
            value: '>=',
            'data-testid': `logicbuilder-number-comparison-select-op-option->=`
          },
          {
            text: 'equal to (=)',
            value: '=',
            'data-testid': `logicbuilder-number-comparison-select-op-option-=`
          },
          {
            text: 'at most (<=)',
            value: '<=',
            'data-testid': `logicbuilder-number-comparison-select-op-option-<=`
          },
          {
            text: 'less than (<)',
            value: '<',
            'data-testid': `logicbuilder-number-comparison-select-op-option-<`
          }
        ]}
      />

      <InputBase
        type="number"
        variant={variant}
        value={liveVal}
        onChange={e => {
          if (e.target.value) {
            setLiveVal(Number(e.target.value));
          } else {
            setLiveVal(null);
          }
        }}
        onBlur={() => updateVal(liveVal)}
        placeholder="Enter value"
      />
    </AlignmentSpan>
  );
};

const YesNoComparison = ({
  updateVal = () => {},
  value,
  variant = 'outlined'
}) => {
  return (
    <Select
      variant={variant}
      data-testid="logicbuilder-yes_no-comparison-select-value"
      onChange={updateVal}
      value={value}
      placeholder="Choose an option..."
      options={['Yes', 'No'].map(v => ({
        text: v,
        value: v,
        'data-testid': `logicbuilder-yes_no-comparison-select-value-option-${v}`
      }))}
    />
  );
};

const ConfirmationComparison = ({
  updateVal = () => {},
  updateOp = () => {},
  value,
  variant = 'outlined'
}) => {
  return (
    <Select
      data-testid="logicbuilder-confirmation-comparison-select-value"
      onChange={val => {
        updateOp(val === 'false' ? 'IS NULL' : '=');
        updateVal(val === 'false' ? undefined : 'true');
      }}
      value={value || 'false'}
      placeholder="Choose an option..."
      variant={variant}
      options={[
        {
          text: 'True',
          value: 'true',
          'data-testid': `logicbuilder-confirmation-comparison-select-value-option-true`
        },
        {
          text: 'False',
          value: 'false',
          'data-testid': `logicbuilder-confirmation-comparison-select-value-option-false`
        }
      ]}
    />
  );
};

const MultipleChoiceComparison = ({
  fields = [],
  form,
  optionsSourceValue = '',
  updateVal = () => {},
  value,
  variant = 'outlined'
}) => {
  const field = fields.find(f => f.internalId === optionsSourceValue) || {};
  let options = field.options || [];
  let optionPairs;
  if (field.uiType === 'status' && form && form.customStatuses) {
    options = form.customStatuses;
  }
  optionPairs = options.map(one => {
    return {
      text: one.label ? one.label : one,
      value: one.internalId ? one.internalId : one
    };
  });

  return (
    <Select
      data-testid="logicbuilder-multiple-choice-comparison-select-value"
      onChange={updateVal}
      value={value}
      variant={variant}
      placeholder={
        optionPairs.find(o => o.value === value) ||
        value === null ||
        typeof value === 'undefined'
          ? 'Choose an option...'
          : 'Option no longer exists!'
      }
      options={optionPairs.map(o => ({
        value: wrapPercents(`||${o.value}||`),
        text: o.text,
        'data-testid': `logicbuilder-multiple-choice-comparison-select-value-option-${o.value}`
      }))}
    />
  );
};

const ExactComparison = ({
  fields = [],
  optionsSourceValue = '',
  updateVal = () => {},
  value,
  variant = 'outlined'
}) => {
  const field = fields.find(f => f.internalId === optionsSourceValue) || {};
  const options = field.options || [];

  return (
    <Select
      data-testid="logicbuilder-exact-comparison-select-value"
      onChange={updateVal}
      value={value}
      variant={variant}
      placeholder="Choose an option..."
      options={options.map(v => ({
        text: v,
        value: v,
        'data-testid': `logicbuilder-exact-comparison-select-value-option-${v}`
      }))}
    />
  );
};

export default FieldsConditionsQueryBuilderView;
